import * as ExcelJS from 'exceljs';
import { ColumnTypes } from './SheetModel';
import { CellValidationHelper } from '../helpers/CellValidationHelper';

export class TemplateModel {
    static createTodoTemplate() {
        const workbook = new ExcelJS.Workbook();
        workbook.properties.title = 'Todo List';
        
        const sheet = workbook.addWorksheet("📝 Todo's");
        sheet.sheetType = 'grid';
        
        // Define columns with their types
        const columns = [
            { heading: 'Title', type: ColumnTypes.SINGLE_LINE_TEXT },
            { heading: 'Description', type: ColumnTypes.MULTI_LINE_TEXT },
            { heading: 'Done', type: ColumnTypes.CHECKBOX },
            { heading: 'Due Date', type: ColumnTypes.DATE }
        ];
        
        // Set up columns and their formatting
        sheet.columns = columns.map(col => ({ header: col.heading, width: 15 }));
        
        // Apply column validations
        columns.forEach((col, index) => {
            const columnIndex = index + 1; // ExcelJS columns are 1-indexed
            CellValidationHelper.setupNewColumnValidation(sheet, columnIndex, col.type);
            
            // For columns with custom validation like checkbox
            if (col.type.hasCustomValidation) {
                CellValidationHelper.updateColumnOptions(
                    sheet, 
                    columnIndex, 
                    col.type.defaultOptions || [],
                    col.type
                );
            }
        });
        
        // Create date objects with correct formatting for the app
        function createFormattedDate(daysFromNow) {
            const date = new Date();
            date.setDate(date.getDate() + daysFromNow);
            
            // Create ISO date string without time component (YYYY-MM-DD)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            
            // Create the date object in the format expected by the app
            return {
                date: `${year}-${month}-${day}`,
                text: `${day}/${month}/${year}`,
                isDateOnly: true
            };
        }
        
        // Add sample data rows with proper formatting
        // Note: Only the third row has Done=true, others are false
        sheet.addRow(['Create project plan', 'Define project scope, timeline, and deliverables', false, createFormattedDate(7)]);
        sheet.addRow(['Schedule team meeting', 'Review progress and assign new tasks', false, createFormattedDate(3)]);
        sheet.addRow(['Prepare presentation', 'Create slides for the client meeting next week', true, createFormattedDate(5)]);
        
        // Store column types in the metadata sheet
        const metaSheet = workbook.addWorksheet('_React2Excel_META_x7z9', { state: 'veryHidden' });
        metaSheet.getCell('A1').value = 'SheetTypes';
        metaSheet.getCell('A2').value = "Todo's";
        metaSheet.getCell('B2').value = 'grid';
        
        // Store column types in a format that SheetDataStore can understand
        metaSheet.getCell('C1').value = 'ColumnTypes';
        columns.forEach((col, index) => {
            metaSheet.getCell(`C${index + 2}`).value = col.heading;
            metaSheet.getCell(`D${index + 2}`).value = col.type.id;
        });
        
        return workbook;
    }
    
    static createBacklogTemplate() {
        const workbook = new ExcelJS.Workbook();
        workbook.properties.title = 'Backlog';
        
        const sheet = workbook.addWorksheet("📝 Backlog");
        sheet.sheetType = 'grid';
        
        // Define columns with their types
        const columns = [
            { heading: 'Title', type: ColumnTypes.SINGLE_LINE_TEXT },
            { heading: 'Description', type: ColumnTypes.MULTI_LINE_TEXT },
            { heading: 'Status', type: ColumnTypes.STATUS },
            { heading: 'Priority', type: ColumnTypes.SELECT },
            { heading: 'Assigned To', type: ColumnTypes.PERSON },
            { heading: 'Due Date', type: ColumnTypes.DATE }
        ];
        
        // Set up columns and their formatting
        sheet.columns = columns.map(col => ({ header: col.heading, width: 15 }));
        
        // Define status and priority options
        const statusOptions = ['Not Started', 'In Progress', 'In Review', 'Done', 'Blocked'];
        const priorityOptions = ['High', 'Medium', 'Low'];
        const personOptions = ['John Smith', 'Jane Doe', 'Alex Johnson', 'Sam Williams'];
        
        // Apply column validations
        columns.forEach((col, index) => {
            const columnIndex = index + 1; // ExcelJS columns are 1-indexed
            CellValidationHelper.setupNewColumnValidation(sheet, columnIndex, col.type);
            
            // For columns with custom validation
            if (col.type.hasCustomValidation) {
                let options = col.type.defaultOptions || [];
                
                // Set custom options based on column heading
                if (col.heading === 'Status') {
                    options = statusOptions;
                } else if (col.heading === 'Priority') {
                    options = priorityOptions;
                } else if (col.heading === 'Assigned To') {
                    options = personOptions;
                }
                
                CellValidationHelper.updateColumnOptions(
                    sheet, 
                    columnIndex, 
                    options,
                    col.type
                );
            }
        });
        
        // Create date objects with correct formatting for the app
        function createFormattedDate(daysFromNow) {
            const date = new Date();
            date.setDate(date.getDate() + daysFromNow);
            
            // Create ISO date string without time component (YYYY-MM-DD)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            
            // Create the date object in the format expected by the app
            return {
                date: `${year}-${month}-${day}`,
                text: `${day}/${month}/${year}`,
                isDateOnly: true
            };
        }
        
        // Add rows with different outline levels
        // Row 1 - Level 0
        const row1 = sheet.addRow(['Frontend Features', 'User interface and experience improvements', 'In Progress', 'High', 'John Smith', createFormattedDate(14)]);
        
        // Rows 2-4 - Level 1
        const row2 = sheet.addRow(['Implement responsive design', 'Make the application work well on mobile devices', 'Not Started', 'High', 'Alex Johnson', createFormattedDate(7)]);
        const row3 = sheet.addRow(['Add dark mode', 'Create a dark theme option for the application', 'Not Started', 'Medium', 'Jane Doe', createFormattedDate(14)]);
        const row4 = sheet.addRow(['Improve accessibility', 'Ensure the app meets WCAG standards', 'In Review', 'Medium', 'John Smith', createFormattedDate(21)]);
        
        // Row 5 - Level 0
        const row5 = sheet.addRow(['Backend Features', 'Server and database improvements', 'Not Started', 'High', 'Sam Williams', createFormattedDate(30)]);
        
        // Rows 6-8 - Level 1
        const row6 = sheet.addRow(['Optimize database queries', 'Improve performance of slow queries', 'Blocked', 'High', 'Alex Johnson', createFormattedDate(14)]);
        const row7 = sheet.addRow(['Implement caching', 'Add Redis cache for frequently accessed data', 'In Progress', 'Medium', 'Sam Williams', createFormattedDate(21)]);
        const row8 = sheet.addRow(['Setup CI/CD pipeline', 'Automate testing and deployment', 'Done', 'Low', 'Jane Doe', createFormattedDate(28)]);
        
        // Set outline levels
        // Row 1 and 5 are level 0 (default)
        // Row 2, 3, 4, 6, 7, 8 are level 1
        row2.outlineLevel = 1;
        row3.outlineLevel = 1;
        row4.outlineLevel = 1;
        row6.outlineLevel = 1;
        row7.outlineLevel = 1;
        row8.outlineLevel = 1;
        
        // Define column style objects using the same format as OptionStyleEditor
        const statusColumnStyles = {
            'Not Started': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } }, 
                font: { color: { argb: 'FF475569' } } 
            },
            'In Progress': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } }, 
                font: { color: { argb: 'FF1D4ED8' } } 
            },
            'In Review': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEFCE8' } }, 
                font: { color: { argb: 'FF854D0E' } } 
            },
            'Done': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } }, 
                font: { color: { argb: 'FF047857' } } 
            },
            'Blocked': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF2F2' } }, 
                font: { color: { argb: 'FFB91C1C' } } 
            }
        };
        
        const priorityColumnStyles = {
            'High': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF2F2' } }, 
                font: { color: { argb: 'FFB91C1C' } } 
            },
            'Medium': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEFCE8' } }, 
                font: { color: { argb: 'FF854D0E' } } 
            },
            'Low': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } }, 
                font: { color: { argb: 'FF047857' } } 
            }
        };
        
        // Add conditional formatting rules for Status column
        const statusColumnLetter = 'C';
        const statusRange = `${statusColumnLetter}2:${statusColumnLetter}${sheet.rowCount}`;
        
        Object.entries(statusColumnStyles).forEach(([status, style], index) => {
            // Create expression rule for each status
            sheet.addConditionalFormatting({
                ref: statusRange,
                rules: [{
                    type: 'expression',
                    formulae: [`${statusColumnLetter}2="${status}"`],
                    style: style,
                    priority: index + 1
                }]
            });
        });
        
        // Add conditional formatting rules for Priority column
        const priorityColumnLetter = 'D';
        const priorityRange = `${priorityColumnLetter}2:${priorityColumnLetter}${sheet.rowCount}`;
        
        Object.entries(priorityColumnStyles).forEach(([priority, style], index) => {
            // Create expression rule for each priority
            sheet.addConditionalFormatting({
                ref: priorityRange,
                rules: [{
                    type: 'expression',
                    formulae: [`${priorityColumnLetter}2="${priority}"`],
                    style: style,
                    priority: index + 1
                }]
            });
        });
        
        // Store column types and metadata
        const metaSheet = workbook.addWorksheet('_React2Excel_META_x7z9', { state: 'veryHidden' });
        
        // Store sheet type
        metaSheet.getCell('A1').value = 'SheetTypes';
        metaSheet.getCell('A2').value = "Backlog";
        metaSheet.getCell('B2').value = 'grid';
        
        // Store column types
        metaSheet.getCell('C1').value = 'ColumnTypes';
        columns.forEach((col, index) => {
            metaSheet.getCell(`C${index + 2}`).value = col.heading;
            metaSheet.getCell(`D${index + 2}`).value = col.type.id;
        });
        
        // Store outline levels in metadata
        metaSheet.getCell('E1').value = 'OutlineLevels';
        for (let i = 2; i <= 9; i++) { // Header row + 8 data rows
            const rowNumber = i - 1; // Adjust for header row
            let outlineLevel = 0;
            
            // Set outline levels according to requirements
            if ([2, 3, 4, 6, 7, 8].includes(rowNumber)) {
                outlineLevel = 1;
            }
            
            metaSheet.getCell(`E${i}`).value = outlineLevel;
        }
        
        return workbook;
    }
    
    static createRACIMatrixTemplate() {
        const workbook = new ExcelJS.Workbook();
        workbook.properties.title = 'RACI Matrix';
        
        const sheet = workbook.addWorksheet("📊 RACI Matrix");
        sheet.sheetType = 'grid';
        
        // Define columns with their types
        const columns = [
            { heading: 'Deliverable or Task', type: ColumnTypes.SINGLE_LINE_TEXT, width: 25 },
            { heading: 'Status', type: ColumnTypes.STATUS, width: 15 },
            { heading: 'Name of Role', type: ColumnTypes.SELECT, width: 15 },
            { heading: 'Name of Role', type: ColumnTypes.SELECT, width: 15 },
            { heading: 'Name of Role', type: ColumnTypes.SELECT, width: 15 },
            { heading: 'Name of Role', type: ColumnTypes.SELECT, width: 15 },
        ];
        
        // Set up columns and their formatting with custom widths
        sheet.columns = columns.map(col => ({ 
            header: col.heading, 
            width: col.width || 15 
        }));
        
        // Define status options and RACI options - ordered properly as R.A.C.I
        const statusOptions = ['Not Started', 'In Progress', 'Completed', 'On Hold', 'Delayed'];
        const raciOptions = ['Responsible', 'Accountable', 'Consulted', 'Informed'];
        
        // First setup basic column types
        columns.forEach((col, index) => {
            const columnIndex = index + 1;
            CellValidationHelper.setupNewColumnValidation(sheet, columnIndex, col.type);
        });
        
        // Apply status options to the Status column
        CellValidationHelper.updateColumnOptions(
            sheet,
            2, // Status column index
            statusOptions,
            ColumnTypes.STATUS
        );
        
        // IMPORTANT: Create all rows with placeholder RACI values first
        
        // Phase 1
        const phase1Row = sheet.addRow(['Phase 1', 'Completed', '', '', '', '']);
        const task1_1 = sheet.addRow(['Task/Deliverable 1', 'Completed', '', '', '', '']); // Placeholder C-F
        const task1_2 = sheet.addRow(['Task/Deliverable 2', 'Completed', '', '', '', '']); // Placeholder C-F
        const task1_3 = sheet.addRow(['Task/Deliverable 3', 'Completed', '', '', '', '']); // Placeholder C-F
        const task1_4 = sheet.addRow(['Task/Deliverable 4', 'Completed', '', '', '', '']); // Placeholder C-F
        
        // Phase 2
        const phase2Row = sheet.addRow(['Phase 2', 'In Progress', '', '', '', '']);
        const task2_1 = sheet.addRow(['Task/Deliverable 1', 'In Progress', '', '', '', '']); // Placeholder C-F
        const task2_2 = sheet.addRow(['Task/Deliverable 2', 'In Progress', '', '', '', '']); // Placeholder C-F
        const task2_3 = sheet.addRow(['Task/Deliverable 3', 'In Progress', '', '', '', '']); // Placeholder C-F
        const task2_4 = sheet.addRow(['Task/Deliverable 4', 'In Progress', '', '', '', '']); // Placeholder C-F
        
        // Phase 3
        const phase3Row = sheet.addRow(['Phase 3', 'Not Started', '', '', '', '']);
        const task3_1 = sheet.addRow(['Task/Deliverable 1', 'Not Started', '', '', '', '']); // Placeholder C-F
        const task3_2 = sheet.addRow(['Task/Deliverable 2', 'Not Started', '', '', '', '']); // Placeholder C-F
        const task3_3 = sheet.addRow(['Task/Deliverable 3', 'Not Started', '', '', '', '']); // Placeholder C-F
        const task3_4 = sheet.addRow(['Task/Deliverable 4', 'Not Started', '', '', '', '']); // Placeholder C-F
        
        // Phase 4
        const phase4Row = sheet.addRow(['Phase 4', 'Not Started', '', '', '', '']);
        const task4_1 = sheet.addRow(['Task/Deliverable 1', 'Not Started', '', '', '', '']); // Placeholder C-F
        const task4_2 = sheet.addRow(['Task/Deliverable 2', 'Not Started', '', '', '', '']); // Placeholder C-F
        const task4_3 = sheet.addRow(['Task/Deliverable 3', 'Not Started', '', '', '', '']); // Placeholder C-F
        const task4_4 = sheet.addRow(['Task/Deliverable 4', 'Not Started', '', '', '', '']); // Placeholder C-F
        
        // Set outline levels AFTER adding all rows
        // Phase rows are level 0 (default)
        // Task rows are level 1
        // Get row numbers (ExcelJS rows are 1-based, headers are row 1)
        const taskRowNumbers = [
            // Phase 1 Tasks
            phase1Row.number + 1, phase1Row.number + 2, phase1Row.number + 3, phase1Row.number + 4,
            // Phase 2 Tasks
            phase2Row.number + 1, phase2Row.number + 2, phase2Row.number + 3, phase2Row.number + 4,
            // Phase 3 Tasks
            phase3Row.number + 1, phase3Row.number + 2, phase3Row.number + 3, phase3Row.number + 4,
            // Phase 4 Tasks
            phase4Row.number + 1, phase4Row.number + 2, phase4Row.number + 3, phase4Row.number + 4,
        ];

        taskRowNumbers.forEach(rowNum => {
            const row = sheet.getRow(rowNum);
            if (row) { // Check if row exists
                row.outlineLevel = 1;
            }
        });

        // NOW set the specific RACI values AND their data validation directly
        // Ensure exactly one R and one A per task row
        const taskData = [
            // Phase 1 (Rows 3-6)
            { row: task1_1.number, raci: ['Responsible', 'Consulted', 'Informed', 'Accountable'] }, // R=C, A=F
            { row: task1_2.number, raci: ['Accountable', 'Informed', 'Responsible', 'Consulted'] }, // R=E, A=C
            { row: task1_3.number, raci: ['Consulted', 'Responsible', 'Accountable', 'Informed'] }, // R=D, A=E
            { row: task1_4.number, raci: ['Informed', 'Accountable', 'Consulted', 'Responsible'] }, // R=F, A=D
            // Phase 2 (Rows 8-11)
            { row: task2_1.number, raci: ['Informed', 'Responsible', 'Accountable', 'Consulted'] }, // R=D, A=E
            { row: task2_2.number, raci: ['Accountable', 'Consulted', 'Informed', 'Responsible'] }, // R=F, A=C
            { row: task2_3.number, raci: ['Consulted', 'Informed', 'Responsible', 'Accountable'] }, // R=E, A=F
            { row: task2_4.number, raci: ['Responsible', 'Accountable', 'Consulted', 'Informed'] }, // R=C, A=D
             // Phase 3 (Rows 13-16)
            { row: task3_1.number, raci: ['Accountable', 'Informed', 'Consulted', 'Responsible'] }, // R=F, A=C
            { row: task3_2.number, raci: ['Consulted', 'Responsible', 'Informed', 'Accountable'] }, // R=D, A=F
            { row: task3_3.number, raci: ['Responsible', 'Consulted', 'Accountable', 'Informed'] }, // R=C, A=E
            { row: task3_4.number, raci: ['Informed', 'Accountable', 'Responsible', 'Consulted'] }, // R=E, A=D
            // Phase 4 (Rows 18-21)
            { row: task4_1.number, raci: ['Consulted', 'Accountable', 'Responsible', 'Informed'] }, // R=E, A=D
            { row: task4_2.number, raci: ['Responsible', 'Informed', 'Consulted', 'Accountable'] }, // R=C, A=F
            { row: task4_3.number, raci: ['Accountable', 'Responsible', 'Informed', 'Consulted'] }, // R=D, A=C
            { row: task4_4.number, raci: ['Informed', 'Consulted', 'Accountable', 'Responsible'] }, // R=F, A=E
        ];

        // Define the data validation object once
        const raciValidation = {
            type: 'list',
            allowBlank: true, 
            formulae: [`"${raciOptions.join(',')}"`]
        };

        taskData.forEach(task => {
            for (let i = 0; i < task.raci.length; i++) {
                // Columns C, D, E, F correspond to indices 3, 4, 5, 6
                const colIndex = 3 + i;
                const cell = sheet.getCell(task.row, colIndex);
                cell.value = task.raci[i];
                // Set validation directly on the cell
                cell.dataValidation = { ...raciValidation }; 
            }
        });

        // --- Apply Conditional Formatting Directly --- 

        // Define status styles for conditional formatting
        const statusColumnStyles = {
            'Not Started': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } }, 
                font: { color: { argb: 'FF475569' } } 
            },
            'In Progress': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } }, 
                font: { color: { argb: 'FF1D4ED8' } } 
            },
            'Completed': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } }, 
                font: { color: { argb: 'FF047857' } } 
            },
            'On Hold': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEFCE8' } }, 
                font: { color: { argb: 'FF854D0E' } } 
            },
            'Delayed': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF2F2' } }, 
                font: { color: { argb: 'FFB91C1C' } } 
            }
        };
        
        // Define RACI styles for conditional formatting
        const raciColumnStyles = {
            'Responsible': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } }, 
                font: { color: { argb: 'FF047857' } } 
            },
            'Accountable': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAF5FF' } }, 
                font: { color: { argb: 'FF7E22CE' } } 
            },
            'Consulted': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } }, 
                font: { color: { argb: 'FF1D4ED8' } } 
            },
            'Informed': { 
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7ED' } }, 
                font: { color: { argb: 'FFC2410C' } } 
            }
        };
        
        // Add conditional formatting for Status column (B)
        const statusColumnLetter = 'B';
        const statusRange = `${statusColumnLetter}2:${statusColumnLetter}${sheet.rowCount}`;
        
        Object.entries(statusColumnStyles).forEach(([status, style], index) => {
            // Use simple relative formula based on top-left cell of the range
            sheet.addConditionalFormatting({
                ref: statusRange,
                rules: [{
                    type: 'expression',
                    formulae: [`${statusColumnLetter}2="${status}"`], // Check top-left cell relatively
                    style: style,
                    priority: index + 1
                }]
            });
        });
        
        // Add conditional formatting for RACI columns (C-F)
        for (let colIndex = 3; colIndex <= 6; colIndex++) {
            const columnLetter = String.fromCharCode(64 + colIndex); // Convert 3 to 'C', 4 to 'D', etc.
            const raciRange = `${columnLetter}2:${columnLetter}${sheet.rowCount}`;
            
            Object.entries(raciColumnStyles).forEach(([raci, style], index) => {
                 // Use simple relative formula based on top-left cell of the range
                 sheet.addConditionalFormatting({
                    ref: raciRange,
                    rules: [{
                        type: 'expression',
                        formulae: [`${columnLetter}2="${raci}"`], // Check top-left cell relatively
                        style: style,
                        priority: index + 1
                    }]
                });
            });
        }
        
        // Store column types and metadata
        const metaSheet = workbook.addWorksheet('_React2Excel_META_x7z9', { state: 'veryHidden' });
        
        // Store sheet type
        metaSheet.getCell('A1').value = 'SheetTypes';
        metaSheet.getCell('A2').value = "RACI Matrix";
        metaSheet.getCell('B2').value = 'grid';
        
        // Store column types
        metaSheet.getCell('C1').value = 'ColumnTypes';
        columns.forEach((col, index) => {
            metaSheet.getCell(`C${index + 2}`).value = col.heading;
            metaSheet.getCell(`D${index + 2}`).value = col.type.id;
        });
        
        // Store outline levels in metadata for all rows
        metaSheet.getCell('E1').value = 'OutlineLevels';
        
        // Iterate through all rows in the sheet to store their outline level
        for (let i = 2; i <= sheet.rowCount; i++) { // Start from row 2 (after header)
             const row = sheet.getRow(i);
            metaSheet.getCell(`E${i}`).value = row.outlineLevel || 0; // Default to 0 if undefined
        }
        
        return workbook;
    }
    
    static createSwotAnalysisTemplate() {
        const workbook = new ExcelJS.Workbook();
        workbook.properties.title = 'SWOT Analysis';

        const sheet = workbook.addWorksheet("📊 SWOT Analysis");
        sheet.sheetType = 'grid';

        // Define columns (Category is now STATUS)
        const columns = [
            { heading: 'Category', type: ColumnTypes.STATUS, width: 15 }, // Changed to STATUS
            { heading: 'Point', type: ColumnTypes.MULTI_LINE_TEXT, width: 40 },
            { heading: 'Priority', type: ColumnTypes.SELECT, width: 12 },
            { heading: 'Notes', type: ColumnTypes.MULTI_LINE_TEXT, width: 30 },
        ];

        sheet.columns = columns.map(col => ({
            header: col.heading,
            width: col.width || 15,
            style: { alignment: { wrapText: col.type === ColumnTypes.MULTI_LINE_TEXT } }
        }));

        // Define options
        const categoryOptions = ['Strengths', 'Weaknesses', 'Opportunities', 'Threats']; // SWOT Categories
        const priorityOptions = ['High', 'Medium', 'Low'];

        // Setup basic column types and validation
        columns.forEach((col, index) => {
            const columnIndex = index + 1;
            CellValidationHelper.setupNewColumnValidation(sheet, columnIndex, col.type);
            // Apply options for STATUS and SELECT columns
            if (col.heading === 'Category') {
                CellValidationHelper.updateColumnOptions(
                    sheet,
                    columnIndex, // Column 1
                    categoryOptions,
                    col.type // STATUS type
                );
            }
            if (col.heading === 'Priority') {
                CellValidationHelper.updateColumnOptions(
                    sheet,
                    columnIndex, // Column 3
                    priorityOptions,
                    col.type // SELECT type
                );
            }
        });

        // Add sample data rows (using the defined categories)
        sheet.addRow(['Strengths', 'Strong brand recognition in the market.', 'High', 'Leverage this in upcoming campaigns.']);
        sheet.addRow(['Strengths', 'Experienced development team.', 'High', '']);
        sheet.addRow(['Strengths', 'Loyal customer base.', 'Medium', 'Engage through loyalty programs.']);

        sheet.addRow(['Weaknesses', 'High operational costs compared to competitors.', 'High', 'Need cost optimization strategy.']);
        sheet.addRow(['Weaknesses', 'Limited marketing budget.', 'Medium', 'Focus on cost-effective channels.']);
        sheet.addRow(['Weaknesses', 'Dependency on a single supplier.', 'Low', 'Explore alternative suppliers.']);

        sheet.addRow(['Opportunities', 'Growing demand for sustainable products.', 'High', 'Develop eco-friendly product line.']);
        sheet.addRow(['Opportunities', 'Potential partnerships with complementary businesses.', 'Medium', 'Identify and approach potential partners.']);
        sheet.addRow(['Opportunities', 'Expansion into new geographic markets.', 'Medium', 'Conduct market research for region X.']);

        sheet.addRow(['Threats', 'Intensifying competition.', 'High', 'Monitor competitor activities closely.']);
        sheet.addRow(['Threats', 'Changing regulatory landscape.', 'Medium', 'Stay informed about new regulations.']);
        sheet.addRow(['Threats', 'Potential economic downturn.', 'Low', 'Develop contingency plans.']);

        // Define styles for categories
        const categoryColumnStyles = {
            'Strengths': {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } }, // Light Green
                font: { color: { argb: 'FF047857' } }
            },
            'Weaknesses': {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF2F2' } }, // Light Red
                font: { color: { argb: 'FFB91C1C' } }
            },
            'Opportunities': {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } }, // Light Blue
                font: { color: { argb: 'FF1D4ED8' } }
            },
            'Threats': {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF7ED' } }, // Light Orange
                font: { color: { argb: 'FFC2410C' } }
            }
        };

        // Add conditional formatting for Category column (A)
        const categoryColumnLetter = 'A';
        const categoryRange = `${categoryColumnLetter}2:${categoryColumnLetter}${sheet.rowCount}`;

        Object.entries(categoryColumnStyles).forEach(([category, style], index) => {
            sheet.addConditionalFormatting({
                ref: categoryRange,
                rules: [{
                    type: 'expression',
                    formulae: [`${categoryColumnLetter}2="${category}"`],
                    style: style,
                    priority: index + 1
                }]
            });
        });

        // Add conditional formatting for Priority column (C)
        const priorityColumnLetter = 'C';
        const priorityRange = `${priorityColumnLetter}2:${priorityColumnLetter}${sheet.rowCount}`;

        const priorityColumnStyles = {
            'High': {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF2F2' } },
                font: { color: { argb: 'FFB91C1C' } }
            },
            'Medium': {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEFCE8' } },
                font: { color: { argb: 'FF854D0E' } }
            },
            'Low': {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } },
                font: { color: { argb: 'FF047857' } }
            }
        };

        Object.entries(priorityColumnStyles).forEach(([priority, style], index) => {
            sheet.addConditionalFormatting({
                ref: priorityRange,
                rules: [{
                    type: 'expression',
                    formulae: [`${priorityColumnLetter}2="${priority}"`],
                    style: style,
                    priority: index + 1
                }]
            });
        });

        // Store metadata
        const metaSheet = workbook.addWorksheet('_React2Excel_META_x7z9', { state: 'veryHidden' });
        metaSheet.getCell('A1').value = 'SheetTypes';
        metaSheet.getCell('A2').value = "SWOT Analysis";
        metaSheet.getCell('B2').value = 'grid';

        metaSheet.getCell('C1').value = 'ColumnTypes';
        columns.forEach((col, index) => {
            metaSheet.getCell(`C${index + 2}`).value = col.heading;
            metaSheet.getCell(`D${index + 2}`).value = col.type.id;
        });

        // Store validation options for Category (Col A) and Priority (Col C)
        metaSheet.getCell('F1').value = 'ColumnValidations';
        metaSheet.getCell('F2').value = 'Category';
        metaSheet.getCell('G2').value = JSON.stringify(categoryOptions);
        metaSheet.getCell('F3').value = 'Priority';
        metaSheet.getCell('G3').value = JSON.stringify(priorityOptions);

        return workbook;
    }

    static createTimeLogTemplate() {
        const workbook = new ExcelJS.Workbook();
        workbook.properties.title = 'Time Log';

        const sheet = workbook.addWorksheet("📊 Time Log");
        sheet.sheetType = 'grid';

        // Define columns (TIME changed to SINGLE_LINE_TEXT)
        const columns = [
            { heading: 'Date', type: ColumnTypes.DATE, width: 12 },
            { heading: 'Project/Task', type: ColumnTypes.SINGLE_LINE_TEXT, width: 25 },
            { heading: 'Description', type: ColumnTypes.MULTI_LINE_TEXT, width: 40 },
            { heading: 'Start Time', type: ColumnTypes.SINGLE_LINE_TEXT, width: 12 }, // Changed to TEXT
            { heading: 'End Time', type: ColumnTypes.SINGLE_LINE_TEXT, width: 12 },   // Changed to TEXT
            { heading: 'Duration', type: ColumnTypes.NUMBER, width: 12 }, 
            { heading: 'Billable', type: ColumnTypes.CHECKBOX, width: 10 },
        ];

        sheet.columns = columns.map((col, index) => ({
            header: col.heading,
            width: col.width || 15,
            style: {
                alignment: {
                    wrapText: col.type === ColumnTypes.MULTI_LINE_TEXT,
                    horizontal: col.type === ColumnTypes.CHECKBOX ? 'center' : undefined,
                },
                // Apply number format for Duration (Hours) and keep Time as text
                numFmt: col.heading === 'Duration' ? '0.00' : undefined 
            }
        }));

        // Setup basic column types and validation
        columns.forEach((col, index) => {
            const columnIndex = index + 1;
            CellValidationHelper.setupNewColumnValidation(sheet, columnIndex, col.type);
            // Checkbox needs explicit setup if helper doesn't handle it automatically
             if (col.type === ColumnTypes.CHECKBOX) {
                CellValidationHelper.updateColumnOptions(
                    sheet,
                    columnIndex,
                    [true, false], // Standard checkbox options
                    col.type
                );
            }
        });
        
        // Helper to create date objects easily
        function createFormattedDate(daysAgo) {
            const date = new Date();
            date.setDate(date.getDate() - daysAgo);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return {
                date: `${year}-${month}-${day}`,
                text: `${day}/${month}/${year}`, // Or keep as Date object if ExcelJS handles it
                isDateOnly: true
            };
        }

        // Helper to create time values (ExcelJS uses fractions of a day) - Now returns string
        function createTimeValue(hours, minutes) {
             // Return as HH:MM string since column type is now text
            return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }

        // Add sample data rows
        sheet.addRow([createFormattedDate(1), 'Project Alpha', 'Development work on feature X', createTimeValue(9, 0), createTimeValue(12, 30), 3.5, true]);
        sheet.addRow([createFormattedDate(1), 'Project Alpha', 'Code review for feature Y', createTimeValue(13, 30), createTimeValue(15, 0), 1.5, true]);
        sheet.addRow([createFormattedDate(1), 'Admin', 'Team meeting and planning', createTimeValue(15, 0), createTimeValue(16, 0), 1.0, false]);
        sheet.addRow([createFormattedDate(0), 'Project Beta', 'Client call regarding requirements', createTimeValue(10, 0), createTimeValue(11, 0), 1.0, true]);
        sheet.addRow([createFormattedDate(0), 'Project Beta', 'Design mockups for UI components', createTimeValue(11, 15), createTimeValue(16, 45), 5.5, true]); // Adjusted duration
        sheet.addRow([createFormattedDate(0), 'Admin', 'Email correspondence', createTimeValue(17, 0), createTimeValue(17, 30), 0.5, false]);

        // Add conditional formatting for Billable column (G)
        const billableColumnLetter = 'G';
        const billableRange = `${billableColumnLetter}2:${billableColumnLetter}${sheet.rowCount}`;

        // TRUE (Billable) - Light Green Background
        sheet.addConditionalFormatting({
            ref: billableRange,
            rules: [{
                type: 'expression',
                formulae: [`${billableColumnLetter}2=TRUE`],
                style: { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFECFDF5' } } }, // Light green fill
                priority: 1
            }]
        });

        // FALSE (Not Billable) - Light Red Background
        sheet.addConditionalFormatting({
            ref: billableRange,
            rules: [{
                type: 'expression',
                formulae: [`${billableColumnLetter}2=FALSE`],
                style: { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF2F2' } } }, // Light red fill
                priority: 2
            }]
        });

        // Store metadata
        const metaSheet = workbook.addWorksheet('_React2Excel_META_x7z9', { state: 'veryHidden' });
        metaSheet.getCell('A1').value = 'SheetTypes';
        metaSheet.getCell('A2').value = "Time Log";
        metaSheet.getCell('B2').value = 'grid';

        metaSheet.getCell('C1').value = 'ColumnTypes';
        columns.forEach((col, index) => {
            metaSheet.getCell(`C${index + 2}`).value = col.heading;
            metaSheet.getCell(`D${index + 2}`).value = col.type.id;
        });

        return workbook;
    }
    
    static getTemplates() {
        return [
            {
                id: 'todo-list',
                name: 'Todo List',
                description: 'A simple todo list with title, description, status, and due date',
                icon: '📋',
                createFn: TemplateModel.createTodoTemplate
            },
            {
                id: 'backlog',
                name: 'Backlog',
                description: 'Project backlog with hierarchical tasks, priorities, and assignments',
                icon: '📊',
                createFn: TemplateModel.createBacklogTemplate
            },
            {
                id: 'raci-matrix',
                name: 'RACI Matrix',
                description: 'Responsibility assignment matrix for project roles and deliverables',
                icon: '📑',
                createFn: TemplateModel.createRACIMatrixTemplate
            },
            {
                id: 'swot-analysis',
                name: 'SWOT Analysis',
                description: 'Analyze Strengths, Weaknesses, Opportunities, and Threats',
                icon: '🧠',
                createFn: TemplateModel.createSwotAnalysisTemplate
            },
            {
                id: 'time-log',
                name: 'Time Log',
                description: 'Log time spent on projects and tasks',
                icon: '⏱️',
                createFn: TemplateModel.createTimeLogTemplate
            }
            // More templates can be added here later
        ];
    }
} 